import { useState, useEffect, useRef } from 'react';
import { DataTable, DataTableExpandedRows, DataTableFilterMeta, DataTableRowEvent, DataTableValueArray } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { RawMaterialMasterService } from '../../service/RawMaterialMasterService';
import RawMaterialMaster from '../../models/RawMaterialMaster';
import { FilterMatchMode } from 'primereact/api';
import RawMaterialGroup from '../../models/RawMaterialGroup';
import { Card } from 'primereact/card';

const RawMaterialMasterView = () => {
    const [rawMaterialMaster, setRawMaterialMaster] = useState<RawMaterialMaster[]>([]);
    const [rawMaterialGroups, setRawMaterialGroups] = useState<RawMaterialGroup[]>([]);
    const [loadingRawMaterialMaster, setLoadingRawMaterialMaster] = useState(true);
    const [loadingRawMaterialGroups, setLoadingRawMaterialGroups] = useState(true);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [filters, setFilters] = useState<DataTableFilterMeta>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const toast = useRef<any>(null);

    useEffect(() => {
        RawMaterialMasterService.getMasterRawMaterials().then(data => {
            setRawMaterialMaster(data);
            setLoadingRawMaterialMaster(false);
        });
    }, []);

    const materialNumberBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.material_Number;
    }

    const oldMaterialNumberBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.old_Material_Number;
    }

    const materialTypeBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.material_Type;
    }

    const industrySectorBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.industry_Sector;
    }

    const descriptionBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.description;
    }

    const baseUomBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.base_Uom;
    }

    const materialGroupBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.material_Group;
    }

    const externalMaterialGroupBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.external_Material_Group;
    }

    const labOfficeBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.lab_Office;
    }

    const divisionBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.division;
    }

    const productHierarchyBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.product_Hierarchy;
    }

    const crossPlantMaterialStatusBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.cross_Plant_Material_Status;
    }

    const crossPlantValidFromDateBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.cross_Plant_Valid_From_Date;
    }

    const generalItemCategoryGroupBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.general_Item_Category_Group;
    }

    const weightUomBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.weight_Uom;
    }

    const weightGrossBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.weight_Gross;
    }

    const weightNetBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.weight_Gross;
    }

    const volumeUomBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.volume_Uom;
    }

    const volumeBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.volume;
    }

    const sizeDimensionBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.size_Dimension;
    }

    const configurableMaterialIndicatorBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.configurable_Material_Indicator;
    }
    
    const batchManagementIndicatorBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.batch_Management_Indicator;
    }

    const purchasingValueKeyBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.purchasing_Value_Key;
    }

    const transportationGroupBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.transportation_Group;
    }

    const serialNumberLevelBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.serial_Number_Level;
    }

    const storageConditionsBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.storage_Conditions;
    }

    const minimumRemainingShelfLifeBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.minimum_Remaining_Shelf_Life;
    }

    const totalShelfLifeBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.total_Shelf_Life;
    }

    const storagePercentageBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.storage_Percentage;
    }

    const periodIndicatorForSledBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.period_Indicator_For_Sled;
    }

    const dangerousGoodsIndicatorProfileBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.dangerous_Goods_Indicator_Profile;
    }

    const environmentallyRelevantBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.environmentally_Relevant;
    }

    const packagingMaterialGroupBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.packaging_Material_Group;
    }

    const packagingMaterialTypeBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.packaging_Material_Type;
    }

    const eanUpcCategoryBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.ean_Upc_Category;
    }

    const eanUpcNumberBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.ean_Upc_Number;
    }

    const allowedPackagingWeightBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.allowed_Packaging_Weight;
    }

    const allowedPackagingWeightUomBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.allowed_Packaging_Weight_Uom;
    }

    const excessWeightToleranceBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.excess_Weight_Tolerance;
    }

    const productAllocationBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.product_Allocation;
    }

    const basicMaterialBodyTemplate = (rowData: RawMaterialMaster) => {
        return rowData.basic_Material;
    }

    const rawMaterialGroupBodyTemplate = (rowData: RawMaterialGroup) => {
        return rowData.raw_Material_Group;
    }

    const rawMaterialGroupDescriptionBodyTemplate = (rowData: RawMaterialGroup) => {
        return rowData.description;
    }

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilter(value);
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-end md:align-items-center">
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText className="p-inputtext-sm" type="search" autoComplete="off" value={globalFilter} onChange={onGlobalFilterChange} placeholder="Search..." />
            </IconField>
        </div>
    );

    const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined);

    const onRowExpand = (event: DataTableRowEvent) => {
        RawMaterialMasterService.getRawMaterialGroupsByMaterialNumber((event.data as RawMaterialMaster).material_Number).then(data => {
            let _rawMaterialMasterList = [...rawMaterialMaster];
            let _rawMaterialMaster = _rawMaterialMasterList.filter((v) => v.material_Number === (event.data as RawMaterialMaster).material_Number)[0];
            _rawMaterialMaster.rawMaterialGroups = data;
            setRawMaterialGroups(data);
            setLoadingRawMaterialGroups(false);
        });
    };

    const rowExpansionTemplate = (data: RawMaterialMaster) => {
        return (
            <div className="p-3 surface-200">
                <DataTable value={data.rawMaterialGroups} loading={loadingRawMaterialGroups}
                    style={{maxWidth : '75rem'}}
                    emptyMessage="No raw material groups found.">
                    <Column field="raw_Material_Group" header="Raw Material Group" body={rawMaterialGroupBodyTemplate} style={{ width: '20%' }}></Column>
                    <Column field="description" header="Description" body={rawMaterialGroupDescriptionBodyTemplate}  style={{ width: '80%' }}></Column>
                </DataTable>
            </div>
        );
    };

    return (
        <div className="grid-nogutter">
            <div className="col-12">
                <Toast ref={toast} />

                <Card className="layout-main-card border-noround">
                    <DataTable value={rawMaterialMaster} dataKey="material_Number" size="small" rowHover={true} loading={loadingRawMaterialMaster}
                        scrollable scrollHeight="calc(100vh - 14.6rem)"
                        expandedRows={expandedRows} onRowToggle={(e) => setExpandedRows(e.data)}
                        onRowExpand={onRowExpand} collapsedRowIcon="pi pi-angle-right text-primary" expandedRowIcon="pi pi-angle-down text-primary"
                        rowExpansionTemplate={rowExpansionTemplate}
                        paginator rows={25} rowsPerPageOptions={[5, 10, 25]} 
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        filters={filters} emptyMessage="No raw material records found." header={header}>
                        <Column expander={true} style={{ width: '3em' }}> </Column>   
                        <Column field="material_Number" header="Material Number" sortable body={materialNumberBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="old_Material_Number" header="Old Material Number" sortable body={oldMaterialNumberBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="material_Type" header="Material Type" sortable body={materialTypeBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="industry_Sector" header="Industry Sector" sortable body={industrySectorBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="description" header="Description" sortable body={descriptionBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="base_Uom" header="Base UOM" sortable body={baseUomBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="material_Group" header="Material Group" sortable body={materialGroupBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="external_Material_Group" header="External Material Group" sortable body={externalMaterialGroupBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="lab_Office" header="Lab Office" sortable body={labOfficeBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="division" header="Division" sortable body={divisionBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="product_Hierarchy" header="Product Hierarchy" sortable body={productHierarchyBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="cross_Plant_Material_Status" header="Cross Plant Material Status" sortable body={crossPlantMaterialStatusBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="cross_Plant_Valid_From_Date" header="Cross Plant Valid From Date" sortable body={crossPlantValidFromDateBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="general_Item_Category_Group" header="General Item Category Group" sortable body={generalItemCategoryGroupBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="weight_Uom" header="Weight UOM" sortable body={weightUomBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="weight_Gross" header="Weight Gross" sortable body={weightGrossBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="weight_Net" header="Weight Net" sortable body={weightNetBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="volume_Uom" header="Volume UOM" sortable body={volumeUomBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="volume" header="Volume" sortable body={volumeBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="size_Dimension" header="Size Dimension" sortable body={sizeDimensionBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="configurable_Material_Indicator" header="Configurable Material Indicator" sortable body={configurableMaterialIndicatorBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="batch_Management_Indicator" header="Batch Management Indicator" sortable body={batchManagementIndicatorBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="purchasing_Value_Key" header="Purchasing Value Key" sortable body={purchasingValueKeyBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="transportation_Group" header="Transportation Group" sortable body={transportationGroupBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="serial_Number_Level" header="Serial Number Level" sortable body={serialNumberLevelBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="storage_Conditions" header="Storage Conditions" sortable body={storageConditionsBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="minimum_Remaining_Shelf_Life" header="Minimum Remaining Shelf Life" sortable body={minimumRemainingShelfLifeBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="total_Shelf_Life" header="Total Shelf Life" sortable body={totalShelfLifeBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="storage_Percentage" header="Storage Percentage" sortable body={storagePercentageBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="period_Indicator_For_Sled" header="Period Indicator For SLED" sortable body={periodIndicatorForSledBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="dangerous_Goods_Indicator_Profile" header="Dangerous Goods Indicator Profile" sortable body={dangerousGoodsIndicatorProfileBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="environmentally_Relevant" header="Environmentally Relevant" sortable body={environmentallyRelevantBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="packaging_Material_Group" header="Packaging Material Group" sortable body={packagingMaterialGroupBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="packaging_Material_Type" header="Packaging Material Type" sortable body={packagingMaterialTypeBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="ean_Upc_Category" header="EAN UPC Category" sortable body={eanUpcCategoryBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="ean_Upc_Number" header="EAN UPC Number" sortable body={eanUpcNumberBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="allowed_Packaging_Weight" header="Allowed Packaging Weight" sortable body={allowedPackagingWeightBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="allowed_Packaging_Weight_Uom" header="Allowed Packaging Weight UOM" sortable body={allowedPackagingWeightUomBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="excess_Weight_Tolerance" header="Excess Weight Tolerance" sortable body={excessWeightToleranceBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="product_Allocation" header="Product Allocation" sortable body={productAllocationBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                        <Column field="basic_Material" header="Basic Material" sortable body={basicMaterialBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    </DataTable>
                </Card>
            </div>
        </div>
    );
}

export default RawMaterialMasterView;